import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAllTickets, updateTicketStatus } from "../../api/support";
import "../../styles/AdminPanel/ManagerTicketList.css";

const TABS = [
  { key: "waiting", label: "در صف پاسخ" },
  { key: "open", label: "تیکت باز" },
  { key: "closed", label: "بسته" },
];

// تیکت در صف پاسخ = آخرین پیام از user یا marketer
function isWaiting(ticket) {
  if (ticket.status === "closed") return false;
  const role = ticket.last_message?.sender_role;
  return role === "user" || role === "marketer";
}

function getDetailedStatus(ticket) {
  if (ticket.status === "closed")
    return { label: "بسته", className: "status-closed" };
  const role = ticket.last_message?.sender_role;
  if (role === "user" || role === "marketer")
    return { label: "در صف پاسخ", className: "msg-waiting" };
  if (role === "support" || role === "admin")
    return { label: "پاسخ داده شده", className: "msg-answered" };
  return { label: "باز", className: "status-open" };
}

function formatRelativeTime(dateString) {
  if (!dateString) return "—";
  const diff = Date.now() - new Date(dateString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "همین الان";
  if (m < 60) return `${m} دقیقه پیش`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ساعت پیش`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} روز پیش`;
  return new Date(dateString).toLocaleDateString("fa-IR");
}

export default function ManagerTicketList() {
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  // ← آدرس صحیح برای admin
  const basePath = role === "admin" ? "/admin/support" : "/support";

  const [activeTab, setActiveTab] = useState("waiting");
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState("");

  const PAGE_SIZE = 20;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  const pageNumbers = [];
  for (let n = 1; n <= totalPages; n++) pageNumbers.push(n);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // "waiting" و "open" هر دو از بک‌اند status=open می‌گیرند
      const statusParam = activeTab === "closed" ? "closed" : "open";
      const res = await getAllTickets({ status: statusParam, page });
      const list = res.data.results ?? res.data;

      // فیلتر client-side برای تب "در صف پاسخ"
      const filtered = activeTab === "waiting" ? list.filter(isWaiting) : list;

      setTickets(filtered);
      setTotalCount(res.data.count ?? list.length ?? 0);
    } catch {
      setError("خطا در دریافت تیکت‌ها.");
      setTickets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleRowClick = (id) => {
    navigate(`${id}`);
  };

  const handleStatusChange = async (e, ticketId) => {
    e.stopPropagation();
    setActionLoading(ticketId);
    try {
      await updateTicketStatus(ticketId, e.target.value);
      await fetchTickets();
    } catch {
      setError("تغییر وضعیت ناموفق بود.");
    } finally {
      setActionLoading(null);
    }
  };

  const Pagination = () =>
    totalCount > 0 && (
      <div className="orders-pagination">
        <span className="pagination-info">
          {totalCount.toLocaleString("fa-IR")} تیکت — صفحه{" "}
          {pageSafe.toLocaleString("fa-IR")} از{" "}
          {totalPages.toLocaleString("fa-IR")}
        </span>
        <div className="pagination-controls">
          <button
            className="page-btn"
            disabled={pageSafe === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="صفحه قبل"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          {pageNumbers.map((n) => (
            <button
              key={n}
              className={`page-btn number${n === pageSafe ? " active" : ""}`}
              onClick={() => setPage(n)}
            >
              {n.toLocaleString("fa-IR")}
            </button>
          ))}
          <button
            className="page-btn"
            disabled={pageSafe === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="صفحه بعد"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>
    );

  return (
    <div className="manager-ticket-list">
      <div className="mtl-header">
        <h2 className="mtl-title">مدیریت تیکت‌ها</h2>

        <div className="status-filter">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`filter-btn ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => {
                setActiveTab(tab.key);
                setPage(1);
              }}
            >
              {tab.label}
              {activeTab === tab.key && (
                <span className="filter-count">{tickets.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="mtl-error">{error}</p>}

      {loading ? (
        <p className="mtl-loading">در حال بارگذاری...</p>
      ) : tickets.length === 0 ? (
        <p className="mtl-empty">تیکتی یافت نشد.</p>
      ) : (
        <div className="mtl-table-wrapper">
          <table className="mtl-table">
            <thead>
              <tr>
                <th>#</th>
                <th>عنوان</th>
                <th>کاربر</th>
                <th>نقش کاربر</th>
                <th>آخرین پیام از</th>
                <th>نام فروشگاه</th>
                <th>وضعیت</th>
                <th>آخرین بروزرسانی</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const ds = getDetailedStatus(ticket);
                const lm = ticket.last_message;
                const senderIsUser =
                  lm?.sender_role === "user" || lm?.sender_role === "marketer";
                return (
                  <tr
                    key={ticket.id}
                    className="mtl-row"
                    onClick={() => handleRowClick(ticket.id)}
                  >
                    <td className="mtl-id" data-label="شناسه">
                      #{ticket.id}
                    </td>
                    <td className="mtl-title-cell" data-label="عنوان">
                      {ticket.title}
                    </td>
                    <td className="mtl-user" data-label="کاربر">
                      {ticket.owner_name ?? "—"}
                    </td>
                    <td className="mtl-owner-role" data-label="نقش">
                      {lm?.sender_role === "marketer"
                        ? "فروشنده"
                        : lm?.sender_role === "user"
                          ? "کاربر عادی"
                          : "—"}
                    </td>
                    <td className="mtl-last-sender" data-label="آخرین پیام">
                      {lm ? (
                        <span
                          className={`sender-badge ${senderIsUser ? "sender-user" : "sender-staff"}`}
                        >
                          {senderIsUser ? "کاربر" : "پشتیبانی"}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="mtl-user" data-label="نام فروشگاه">
                      {ticket.store}
                    </td>
                    <td data-label="وضعیت">
                      <span className={`mtl-status-badge ${ds.className}`}>
                        {ds.label}
                      </span>
                    </td>
                    <td className="mtl-date" data-label="بروزرسانی">
                      {formatRelativeTime(
                        ticket.updated_at || ticket.created_at,
                      )}
                    </td>
                    <td
                      onClick={(e) => e.stopPropagation()}
                      data-label="عملیات"
                    >
                      <select
                        className="mtl-status-select"
                        value={ticket.status}
                        disabled={actionLoading === ticket.id}
                        onChange={(e) => handleStatusChange(e, ticket.id)}
                      >
                        <option value="open">باز</option>
                        <option value="closed">بسته</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && <Pagination />}
    </div>
  );
}
